import type { SegmentAnalytics } from '@segment/analytics.js-core';
import RecoilizeDebugger from 'recoilize';
import { Suspense } from 'react';
import { createRoot } from 'react-dom/client';
import ReactModal from 'react-modal';
import { BrowserRouter as Router } from 'react-router-dom';
import { RecoilRoot } from 'recoil';
import { Provider } from 'jotai';
import { useHydrateAtoms } from 'jotai/react/utils';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { queryClientAtom } from 'jotai-tanstack-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { NuqsAdapter } from 'nuqs/adapters/react-router/v6';

import { AuthProvider } from '@/components/sparrow-route/view-metrics-route';
import { jotaiStore } from './jotai-store';
import App from './app';
import { environment } from './constants';
import {
  isChunkLoadError,
  shouldRefreshForChunkError,
  markChunkErrorRefresh,
} from './utils/chunk-load-error';

import Loader from './components/loader';

import '../i18n';
import './index.css';
import AppDashboardSkeleton from './components/AppDashboardSkeleton';

// Global types
declare global {
  interface Window {
    customReport: boolean;
    $crisp: any[];
    analytics: SegmentAnalytics.AnalyticsJS & { load: (key: string) => void };
    toltReferral?: string;
  }
}
// End Global Types

// render the application component to the `root` element
const container = document.getElementById('root');

if (!container) {
  throw new Error('No container found');
}

ReactModal.setAppElement(container);
// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes
      retry: 1,
    },
  },
});

const HydrateAtoms = ({ children }: { children: React.ReactNode }) => {
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  useHydrateAtoms([[queryClientAtom, queryClient]]);
  return children;
};

const ConnectedApp = () => (
  <QueryClientProvider client={queryClient}>
    {/* <ReactQueryDevtools initialIsOpen={false} /> */}
    <Provider store={jotaiStore}>
      <HydrateAtoms>
        <RecoilRoot>
          {/* <RecoilizeDebugger /> */}

          <Suspense fallback={<AppDashboardSkeleton show />}>
            {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
            {/* @ts-ignore */}
            <Router>
              <NuqsAdapter>
                <AuthProvider>
                  <App />
                </AuthProvider>
              </NuqsAdapter>
            </Router>
          </Suspense>
        </RecoilRoot>
      </HydrateAtoms>
    </Provider>
  </QueryClientProvider>
);

window.customReport = environment !== 'production';
/* End of switch */

// Handle chunk load errors from dynamic imports outside React lifecycle
// This catches errors that occur when lazy-loaded modules fail after a deployment
window.addEventListener(
  'unhandledrejection',
  (event: PromiseRejectionEvent) => {
    if (isChunkLoadError(event.reason)) {
      event.preventDefault(); // Prevent the error from appearing in console
      if (shouldRefreshForChunkError()) {
        console.log(
          'Chunk load error detected, refreshing to load new version...'
        );
        markChunkErrorRefresh();
        window.location.reload();
      }
    }
  }
);

const root = createRoot(container);
root.render(<ConnectedApp />);
